import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { PageHeader, DataTable, fmt } from "../components/bits";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const MONTHS = [
  ["01", "Enero"], ["02", "Febrero"], ["03", "Marzo"], ["04", "Abril"],
  ["05", "Mayo"], ["06", "Junio"], ["07", "Julio"], ["08", "Agosto"],
  ["09", "Septiembre"], ["10", "Octubre"], ["11", "Noviembre"], ["12", "Diciembre"],
];
const YEARS = ["2025", "2026", "2027"];

export default function PlatformPayments() {
  const now = new Date();
  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState("all");
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    const qs = `?year=${year}${month !== "all" ? `&month=${month}` : ""}`;
    api.get(`/platform/payments${qs}`).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [year, month]);
  useEffect(() => { load(); }, [load]);

  const monthName = (m) => MONTHS.find(([v]) => v === m)?.[1] || m;

  return (
    <div data-testid="platform-payments-page">
      <PageHeader title="Pagos de suscripciones" subtitle="Ingresos de la plataforma por mes o año">
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="w-40" data-testid="payments-month-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todo el año</SelectItem>
            {MONTHS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={year} onValueChange={setYear}>
          <SelectTrigger className="w-28" data-testid="payments-year-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
        </Select>
      </PageHeader>

      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="stat-year-total">
              <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700 mb-1">Total {year}</p>
              <p className="text-xl font-bold text-slate-900">{fmt(data.year_total)}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="stat-period-total">
              <p className="text-xs font-semibold uppercase tracking-wider text-blue-800 mb-1">
                {month === "all" ? "Total del año" : `Total ${monthName(month)}`}
              </p>
              <p className="text-xl font-bold text-slate-900">{fmt(data.period_total)}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="stat-payment-count">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Pagos</p>
              <p className="text-xl font-bold text-slate-900">{data.payments.length}</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="stat-months-active">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Meses con ingresos</p>
              <p className="text-xl font-bold text-slate-900">{data.by_month.length}</p>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm mb-6">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Ingresos por mes — {year}</p>
            <div className="flex flex-wrap gap-3">
              {data.by_month.map((m) => (
                <button
                  key={m.month}
                  data-testid={`month-chip-${m.month}`}
                  onClick={() => setMonth(m.month.slice(5))}
                  className={`px-4 py-2 rounded-lg border text-sm transition-colors ${month === m.month.slice(5) ? "bg-blue-800 text-white border-blue-800" : "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100"}`}
                >
                  {monthName(m.month.slice(5))}: <strong>{fmt(m.total)}</strong>
                </button>
              ))}
              {data.by_month.length === 0 && <p className="text-sm text-slate-400">Sin pagos este año.</p>}
            </div>
          </div>

          <DataTable testid="platform-payments-table" rows={data.payments} columns={[
            { key: "paid_date", label: "Fecha", render: (p) => <span className="font-mono text-xs">{p.paid_date}</span> },
            { key: "invoice_number", label: "Factura", render: (p) => <span className="font-mono text-xs">{p.invoice_number}</span> },
            { key: "school_name", label: "Colegio", render: (p) => <span className="font-medium text-slate-900">{p.school_name}</span> },
            { key: "plan_name", label: "Plan" },
            { key: "period", label: "Período", render: (p) => <span className="font-mono text-xs">{p.period}</span> },
            { key: "method", label: "Método" },
            { key: "reference", label: "Referencia", render: (p) => <span className="font-mono text-xs">{p.reference || "—"}</span> },
            { key: "amount", label: "Monto", render: (p) => <span className="font-semibold text-emerald-700">{fmt(p.amount)}</span> },
          ]} />
        </>
      )}
    </div>
  );
}
